/**
 * BFF auth context – uses session cookie only. No tokens in the browser.
 * Auth state comes from GET /api/session/me. Login redirects to backend /api/auth/login.
 */
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { setSigningOut } from "../utils/authFetch";
import { readReferralCode, storeReferral } from "../utils/referralLink";

const AuthContext = createContext(null);

const baseUrl = () => process.env.REACT_APP_API_URL || "";
// A failed session check is retried before giving up, then re-asked later instead of signing the user out.
const SESSION_CHECK_TRIES = 3;
const SESSION_CHECK_GAP_MS = 1500;
const SESSION_CHECK_BUDGET_MS = 10 * 1000;
const SESSION_RECHECK_MS = 30 * 1000;
const isSameUser = (a, b) =>
  (a?.sub ?? null) === (b?.sub ?? null) &&
  (a?.name ?? null) === (b?.name ?? null) &&
  (a?.picture ?? null) === (b?.picture ?? null) &&
  (a?.email ?? null) === (b?.email ?? null);

// Clear any Auth0 token cache from localStorage (BFF: tokens must never be in browser)
function clearAuth0Cache() {
  try {
    const keys = Object.keys(localStorage);
    keys.forEach((k) => {
      if (k.includes("auth0") || k.includes("@@auth0spajs"))
        localStorage.removeItem(k);
    });
  } catch (_) {}
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [sessionExpiresAt, setSessionExpiresAt] = useState(null);
  // Server-side feature switches ({ jobImport, addCandidates }); a missing flag means the feature is off.
  const [features, setFeatures] = useState({});
  const [sessionInvalidated, setSessionInvalidated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    clearAuth0Cache();
  }, []);

  // Resolves true (signed in), false (the server says there is no session) or null (couldn't tell).
  const checkSession = useCallback(async () => {
    try {
      // Use plain fetch – don't trigger auth-failure redirect on initial load (no session yet).
      // Other API calls use fetchWithAuth and will redirect on 401 when session expires.
      const base = baseUrl();
      const startedAt = Date.now();
      for (let attempt = 1; ; attempt++) {
        const res = await fetch(`${base}/api/session/me`, {
          credentials: "include",
        }).catch(() => null);
        if (res?.ok) {
          const data = await res.json();
          const nextUser = data.user ?? { sub: data.userId };
          setUser((prev) => (isSameUser(prev, nextUser) ? prev : nextUser));
          setSessionExpiresAt(data.expiresAt ?? null);
          setFeatures(data.features ?? {});
          setSessionInvalidated(false);
          return true;
        }
        if (res && (res.status === 401 || res.status === 403)) {
          setUser(null);
          setSessionExpiresAt(null);
          // Force reauth on any 401: covers both session deleted (cookie sent) and time expiry (cookie
          // not sent – browser expired it). When cookie expires, we never receive it, so we can't
          // distinguish; treat all 401s as invalidated so user must enter credentials.
          setSessionInvalidated(true);
          return false;
        }
        // Any other failure (server error, rate limit, dropped connection) says nothing about the session: retry, then leave the user as they are.
        // A server that is slow to fail is not retried, so the first page load never waits on it three times over.
        if (attempt >= SESSION_CHECK_TRIES || Date.now() - startedAt > SESSION_CHECK_BUDGET_MS) return null;
        await new Promise((resolve) => setTimeout(resolve, SESSION_CHECK_GAP_MS));
      }
    } catch {
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  // Re-check session when user returns to tab (catches expiry while tab was in background)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && user) {
        checkSession();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [user, checkSession]);

  // `reauth` forces fresh credentials; `loginHint` prefills the email on Auth0's page.
  const login = useCallback(({ reauth = false, loginHint = "" } = {}) => {
    const params = new URLSearchParams();
    if (reauth) params.set("reauth", "1");
    if (loginHint) params.set("login_hint", loginHint);
    // A referral link's code goes with the login: it only counts if this login creates a new (Google/Microsoft) account.
    const ref = readReferralCode();
    if (ref) params.set("ref", ref);
    storeReferral(null);
    const qs = params.toString() ? `?${params.toString()}` : "";
    window.location.href = `${baseUrl()}/api/auth/login${qs}`;
  }, []);

  // Proactively detect session expiry when expiresAt passes (no need to wait for an API call)
  useEffect(() => {
    if (!sessionExpiresAt) return;
    let cancelled = false;
    let timer;
    const verify = () =>
      checkSession().then((ok) => {
        if (cancelled) return;
        if (ok === false) login({ reauth: true });
        // Server unreachable: stay signed in and ask again shortly.
        else if (ok === null) timer = setTimeout(verify, SESSION_RECHECK_MS);
      });
    const delay = new Date(sessionExpiresAt).getTime() - Date.now();
    timer = setTimeout(verify, Math.max(0, delay));
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [sessionExpiresAt, checkSession, login]);

  const logout = useCallback(() => {
    setSigningOut(true);
    window.location.href = `${baseUrl()}/api/auth/logout`;
  }, []);

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading: loading,
    login,
    logout,
    checkSession,
    sessionExpiresAt,
    sessionInvalidated,
    features,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
